import { buildQuoteSubscriptionRows } from '~/components/PersonalCoverage/JourneyQuote2026/buildQuoteSubscriptionRows';
import type { JourneyQuoteSubscriptionRow } from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteSummaryColumn';
import type { RenewalQuote } from '~/services/modules/renewal';
import type { Quote as AccordionItem } from '~/store/slices/quote/types';
import type { FinancialData } from '../interfaces/financial-data';

export type { AccordionItem as RenewalCoverageItem };

type RenewalProduct = RenewalQuote['contract_item'][number];

/** Item do contrato que segue na renovação (`is_checked` ausente conta como marcado, como no legado). */
const keepsCoverage = (product: RenewalProduct) => product.is_checked !== false;

const toItem = (product: RenewalProduct, required: boolean): AccordionItem => ({
  product_id: String(product.product_id),
  product_name: product.product_name,
  product_description: product.product_description ?? '',
  product_price: Number(product.product_price ?? 0),
  checked: required || Boolean(product.is_checked),
  required,
  cover: product.cover ?? []
});

/**
 * «Coberturas do seu contrato» (Figma 2086:7593): o clube (quando há mensalidade) e os itens do contrato,
 * todos marcados e fixos — a renovação mantém o que o cliente já tem.
 */
export function getRenewalCoverageItems(quote: RenewalQuote): AccordionItem[] {
  const plan = quote.plan_details;
  const flatRate = Number(quote.contract_details?.flat_rate ?? 0);
  const club: AccordionItem[] =
    plan?.plan_has_club && plan.plan_club_name && flatRate > 0
      ? [
          {
            product_id: 'plan-club',
            product_name: plan.plan_club_name,
            product_description: plan.plan_club_description ?? '',
            product_price: flatRate,
            checked: true,
            required: true,
            cover: []
          }
        ]
      : [];
  return [...club, ...(quote.contract_item ?? []).filter(keepsCoverage).map((product) => toItem(product, true))];
}

/** «Aproveite para reforçar sua proteção» (Figma 2088:8571): adicionais opcionais; vazio sem oferta. */
export function getRenewalUpsellItems(quote: RenewalQuote): AccordionItem[] {
  return (quote.upsell ?? []).map((product) => toItem(product, false));
}

/** Produtos da cotação: itens do contrato e upsell já marcado. */
export function getRenewalInitialSelection(quote: RenewalQuote): number[] {
  return [
    ...(quote.contract_item ?? []).filter(keepsCoverage),
    ...(quote.upsell ?? []).filter((product) => product.is_checked)
  ].map((product) => product.product_id);
}

/** Totais da cotação. `total` é o valor mensal; `financial_charges` são os custos operacionais. */
export function buildRenewalFinancials(quote: RenewalQuote): FinancialData {
  const details = quote.contract_details;
  return {
    total: Number(details?.total ?? 0),
    financial_charges: Number(details?.financial_charges ?? 0),
    subtotal: Number(details?.subtotal ?? 0),
    installments: Number(details?.installments ?? 12),
    flat_rate: Number(details?.flat_rate ?? 0),
    type_club: quote.plan_details?.type_club,
    plan_club_name: quote.plan_details?.plan_club_name,
    plan_has_club: quote.plan_details?.plan_has_club ?? false,
    plan_club_description: quote.plan_details?.plan_club_description
  };
}

/** Linhas do resumo (clube + produtos selecionados) com o mesmo builder da cotação 2026. */
export function buildRenewalSummaryRows(
  quote: RenewalQuote,
  selectedIds: number[],
  financials: FinancialData
): JourneyQuoteSubscriptionRow[] {
  const selected = [...(quote.contract_item ?? []), ...(quote.upsell ?? [])].filter((product) =>
    selectedIds.includes(product.product_id)
  );
  return buildQuoteSubscriptionRows({
    products: selected.map((product) => ({
      product_id: String(product.product_id),
      product_name: product.product_name,
      product_price: Number(product.product_price ?? 0)
    })),
    flat_rate: financials.flat_rate,
    plan_club_name: financials.plan_club_name,
    plan_club_description: financials.plan_club_description,
    plan_has_club: financials.plan_has_club
  });
}
